import React, { useEffect, useMemo, useState } from 'react';
import { useUserContext } from "../../../revised_components/contextProviders/userContext";
import { getCollectionList } from "../../../../services/dbGalleryRequests";

// MUI Joy UI imports
import Box from '@mui/joy/Box';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import Input from '@mui/joy/Input';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import LinearProgress from '@mui/joy/LinearProgress';
import Divider from '@mui/joy/Divider';
import SearchIcon from '@mui/icons-material/Search';
import CollectionsIcon from '@mui/icons-material/Collections';
import ImageIcon from '@mui/icons-material/Image';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

// Import CSS
import '../../../../assets/css/revised_css/components/controlManagerThemes/headerWrappers/reports_v2.css'
export function ImportModal({ open, onClose, onClickEntry }) {
  const { user } = useUserContext()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [filter, setFilter] = useState('')

  useEffect(() => {
    let timeout_id
    if (open === true) {
      setLoading(true)
      timeout_id = setTimeout(async () => {
        try {
          let collections = await getCollectionList(user.token)
          console.log('got from collectionList', collections)
          setData(collections.standaloneSets || [])
        } catch (error) {
          console.error('Error loading collections:', error)
          setData([])
        } finally {
          setLoading(false)
        }
      }, 100)
    }
    return () => clearTimeout(timeout_id)
  }, [open, user.token])

  const filteredData = useMemo(() => {
    if (!filter) return data
    return data.filter(item => 
      item.name.toLowerCase().includes(filter.toLowerCase())
    )
  }, [data, filter])

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog 
        size="lg" 
        sx={{ 
          minHeight: 600,
          maxHeight: '80vh',
          width: '80vw',
        }}
      >
        <ModalClose />
        
        {/* Header Section */}
        <Box sx={{ mb: 2 }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CollectionsIcon sx={{ color: '#589A99' }} />
            DBGallery Collections (Projects) Import
          </DialogTitle>
          
          {/* Collections Counter */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ImageIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
            <Typography level="body-sm" color="neutral">
              {loading ? 'Loading...' : `${filteredData.length} collection${filteredData.length !== 1 ? 's' : ''} available`}
            </Typography>
            {filter && (
              <Typography level="body-xs" color="neutral">
                (filtered from {data.length} total)
              </Typography>
            )}
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Search Section */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Input
              placeholder="Type here to filter collections..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              startDecorator={<SearchIcon />}
              size="sm"
              className="input-custom-focus"
              sx={{ width: '100%' }}
            />
          </Box>


          {/* Loading State */}
          {loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <LinearProgress 
                sx={{ 
                  mb: 2,
                  '--LinearProgress-color': '#589A99',
                  '--LinearProgress-trackColor': 'rgba(88, 154, 153, 0.2)'
                }} 
              />
              <Typography level="body-sm" color="neutral">
                Loading collections from DBGallery...
              </Typography>
            </Box>
          )}

          {/* Collections List */}
          {!loading && (
            <Sheet 
              variant="outlined" 
              sx={{ 
                maxHeight: 400, 
                overflow: 'auto',
                borderRadius: 0,
                border: 'none',
                borderTop: '1px solid',
                borderColor: 'divider',
                overflowX: 'hidden',
                flexGrow: 1
              }}
            >
              <List sx={{ p: 0 }}>
                {filteredData.length === 0 ? (
                  <ListItem sx={{ p: 3, textAlign: 'center', margin: 'auto' }}>
                    <Box>
                      <CollectionsIcon sx={{ fontSize: '48px', color: 'text.tertiary', mb: 1 }} />
                      <Typography level="body-md" color="neutral">
                        {filter ? 'No collections match your search' : 'No collections available'}
                      </Typography>
                      {filter && (
                        <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                          Try adjusting your search terms
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                ) : (
                  filteredData.map((item, index) => (
                    <ListItem key={item.id || index} sx={{ p: 0 }}>
                      <ListItemButton 
                        onClick={() =>  onClickEntry(item) }
                        disabled={false && (!item.imageCount || item.imageCount === 0)}
                        className="list-item-custom"
                        sx={{ 
                          borderRadius: 0,
                          p: 2,
                          '&:hover': {
                            backgroundColor:  '#d8e6e5 !important' ,
                            color:  '#589A99 !important'
                          },
                          '&.Mui-disabled': {
                            opacity: 0.5,
                            cursor: 'not-allowed'
                          }
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ImageIcon sx={{ fontSize: '16px' }} />
                            <Typography level="title-sm" sx={{ fontWeight: 'md' }}>
                              <span >{item.name}</span>
                              <span hidden style={{opacity: '0.5'}}> ({item.imageCount} image{item.imageCount !== 1 ? 's' : ''}
                              {(!item.imageCount || item.imageCount === 0) && (
                                <span level="body-xs" style={{ 'font-style': 'italic' }}>
                                  &nbsp;-&nbsp;Nothing to import
                                </span>
                              )}
                            )</span>
                            </Typography>
                           
                          </Box>
                         
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            </Sheet>
          )}
        </DialogContent>

          {/* Info Block */}
          <Box sx={{ 
          p: 2, 
          mb: 1, 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #e9ecef', 
          borderRadius: '8px',
          mx: 1
        }}>
           <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
             <LightbulbIcon sx={{ fontSize: '20px', color: '#589A99', mt: 0.2 }} />
            <Box>
              <Typography level="body-sm" sx={{ fontWeight: 'md', color: '#589A99', mb: 0.5 }}>
                Import Recommendation
              </Typography>
              <Typography level="body-xs" color="neutral">
                The collections must contain subcollections that represent <b>Sections</b> to meet report requirements for import. 
                Only collections with images will be imported.
              </Typography>
            </Box>
          </Box>
        </Box>

      </ModalDialog>
    </Modal>
  )
}
